import PropTypes from 'prop-types';
import React from 'react';

import {
  DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE,
  P1D, PT30M,
  SOURCE_HISTORIAN, SOURCE_TRADES,
  UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from 'src/util/constants';

import PropertyShowChart from './PropertyShowChart';
import PropertyShowTradeSummary from './PropertyShowTradeSummary';

/**
 * Renders the PropertyShowTradeEnergy component, combining the chart
 * and trade summary components.
 * @param {object} props - The component props.
 * @param {object} props.chartView - Chart view configuration.
 * @param {Function} props.controlSetStateFunc - Function to update control state.
 * @param {Function} props.handleTimespanUpdate - Callback when timespan changes.
 * @param {P1D | PT30M} props.historianAggregation - Aggregation level for historian data.
 * @param {object} props.mainData - The main property data.
 * @param {string} [props.noDataMessage] - Optional custom message to display
 * when trade data is empty, overriding the default no-data message.
 * @param {Function} [props.onChartReadyFunc] - Optional callback when chart is ready.
 * @param {SOURCE_HISTORIAN | SOURCE_TRADES} props.source - Data source.
 * @param {object} props.timespan - The time range to display.
 * @param {string} props.timezone - Timezone for date formatting.
 * @param {P1D | PT30M} props.tradeAggregation - Aggregation level for trade data.
 * @param {UNIT_ENERGY | UNIT_CARBON | UNIT_CURRENCY} props.unit - Determines the chart's
 * y-axis unit.
 * @returns {React.ReactElement} The property trade energy view.
 */
function PropertyShowTradeEnergy({
  chartView, controlSetStateFunc, handleTimespanUpdate,
  historianAggregation, mainData, noDataMessage = null, onChartReadyFunc = null, source,
  timespan, tradeAggregation, timezone, unit,
}) {
  return (
    <>
      <PropertyShowChart
        mainData={mainData}
        historianAggregation={historianAggregation}
        noDataMessage={noDataMessage}
        onChartReadyFunc={onChartReadyFunc}
        timespan={timespan}
        timezone={timezone}
        tradeAggregation={tradeAggregation}
        handleTimespanUpdate={handleTimespanUpdate}
        unit={unit}
        source={source}
        controlSetStateFunc={controlSetStateFunc}
        chartView={chartView}
      />
      <PropertyShowTradeSummary
        mainData={mainData}
      />
    </>
  );
}

PropertyShowTradeEnergy.propTypes = {
  controlSetStateFunc: PropTypes.func.isRequired,
  chartView: PropTypes.shape({
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE]).isRequired,
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_PROPERTY,
      DATA_AGGREGATE_BY_METER]).isRequired,
  }).isRequired,
  handleTimespanUpdate: PropTypes.func.isRequired,
  historianAggregation: PropTypes.string.isRequired,
  mainData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  noDataMessage: PropTypes.string,
  onChartReadyFunc: PropTypes.func,
  source: PropTypes.oneOf([SOURCE_HISTORIAN, SOURCE_TRADES]).isRequired,
  timespan: PropTypes.shape({
    start: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    finish: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  timezone: PropTypes.string.isRequired,
  tradeAggregation: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
};

export default React.memo(PropertyShowTradeEnergy);
